import { z } from "zod";

/**
 * Shared DTO — SUPER_ADMIN chuyển chủ nhóm (@UC9870) & đổi vai thành viên nhóm (@UC9871) GIÚP người dùng
 * (US-AE4.F6-0856). Một nguồn sự thật cho api (`POST /v1/admin/groups/:id/ownership/transfer`,
 * `POST /v1/admin/groups/:id/members/:memberId/role`) và admin web (dialog trong GroupDetailPage).
 *
 * 🔴 QĐ người dùng 25/09/2026 (story `US-AE4.F6-0856` §1.3) — đọc trước khi "sửa lại cho chặt":
 * - QĐ-1: một người duyệt, `reason` BẮT BUỘC.
 * - QĐ-5: `supportTicketId` + `requestedByMemberId` TUỲ CHỌN — chỉ vào audit, KHÔNG vào thông báo (QĐ-7).
 * - Nâng lên `owner` CHỈ qua chuyển chủ: body đổi vai không nhận `owner` (trạng thái sai không biểu diễn được).
 * - Chuyển chủ (US-AE4.F7-1011, QĐ người dùng 07/10/2026 — như app, THAY QĐ-13 "hai bên đổi vai"): chủ cũ → Quản trị;
 *   chủ cũ không nhận được vai quản lý (QĐ-14) ⇒ Thành viên (không chặn — cứu nhóm). KHÔNG có ô chọn vai cho chủ cũ.
 * - QĐ-14: vai Quản trị / Thủ quỹ / Chủ nhóm chỉ giao cho suất có NGƯỜI ĐỨNG TÊN TÀI KHOẢN CHÍNH còn hoạt
 *   động (app chỉ trao quyền cho người đó; phụ huynh gắn thêm luôn là Thành viên). Chỉ chủ MỚI / người được đổi vai
 *   phải nhận được vai; không nhận được ⇒ lỗi, không đổi gì.
 *
 * Request `.strict()` — actor lấy từ phiên admin, KHÔNG bao giờ từ body.
 */

/** Độ dài tối đa lý do — cùng giới hạn các thao tác admin nhóm khác (`SUSPEND_GROUP_REASON_MAX_LENGTH`). */
export const ADMIN_GROUP_ROLE_REASON_MAX_LENGTH = 500;

/** Vai gán được qua đổi vai / cho chủ cũ sau chuyển chủ — KHÔNG có `owner`. */
export const adminAssignableGroupRoleSchema = z.enum(["admin", "treasurer", "member"]);
export type AdminAssignableGroupRole = z.infer<typeof adminAssignableGroupRoleSchema>;

/** Vai đầy đủ (dùng trong `changes` — trước/sau có thể là `owner`). */
const groupRoleValueSchema = z.enum(["owner", "admin", "treasurer", "member"]);

const reasonSchema = z.string().trim().min(1).max(ADMIN_GROUP_ROLE_REASON_MAX_LENGTH);

/** Body POST /v1/admin/groups/:id/ownership/transfer. */
export const adminTransferOwnershipBodySchema = z
  .object({
    /** GroupMember.id của suất sẽ làm chủ (KHÔNG phải userId). */
    newOwnerMemberId: z.string().uuid(),
    reason: reasonSchema,
    supportTicketId: z.string().uuid().optional(),
    requestedByMemberId: z.string().uuid().optional(),
  })
  .strict();
export type AdminTransferOwnershipBody = z.infer<typeof adminTransferOwnershipBodySchema>;
export type AdminTransferOwnershipBodyInput = z.input<typeof adminTransferOwnershipBodySchema>;

/** Body POST /v1/admin/groups/:id/members/:memberId/role (`:memberId` = GroupMember.id). */
export const adminSetMemberRoleBodySchema = z
  .object({
    role: adminAssignableGroupRoleSchema,
    reason: reasonSchema,
    supportTicketId: z.string().uuid().optional(),
    requestedByMemberId: z.string().uuid().optional(),
  })
  .strict();
export type AdminSetMemberRoleBody = z.infer<typeof adminSetMemberRoleBodySchema>;

/** Một suất đổi vai trong lượt — liệt kê MỌI suất bị ảnh hưởng (gồm thủ quỹ bị thay). */
export const adminGroupRoleChangeItemSchema = z.object({
  groupMemberId: z.string(),
  before: groupRoleValueSchema,
  after: groupRoleValueSchema,
});
export type AdminGroupRoleChangeItem = z.infer<typeof adminGroupRoleChangeItemSchema>;

/** Response chung cho cả hai endpoint. */
export const adminGroupRoleChangeResponseSchema = z.object({
  groupId: z.string(),
  changes: z.array(adminGroupRoleChangeItemSchema),
});
export type AdminGroupRoleChangeResponse = z.infer<typeof adminGroupRoleChangeResponseSchema>;

/** Mã lỗi ổn định (problem+json `code`) — client map `code` → câu. */
export const ADMIN_GROUP_ROLE_CODE = {
  /** Suất đích chưa có người đứng tên tài khoản chính ⇒ không nhận được vai quản lý (QĐ-14). */
  TARGET_NO_PRIMARY_ACCOUNT: "ADMIN_GROUP_ROLE_TARGET_NO_PRIMARY_ACCOUNT",
  /** Tài khoản chính của suất đích đã xoá (ẩn danh) hoặc đang bị tạm khoá ⇒ chỉ làm Thành viên (QĐ-11). */
  TARGET_ACCOUNT_INACTIVE: "ADMIN_GROUP_ROLE_TARGET_ACCOUNT_INACTIVE",
  // US-AE4.F7-1011: bỏ PREVIOUS_OWNER_* — chủ cũ không nhận được Quản trị nay xuống Thành viên, không còn chặn.
  /** Đổi vai nhắm vào chủ nhóm — phải dùng chuyển chủ. */
  TARGET_IS_OWNER: "ADMIN_GROUP_ROLE_TARGET_IS_OWNER",
  /** Chuyển chủ cho chính chủ hiện tại. */
  ALREADY_OWNER: "ADMIN_GROUP_ROLE_ALREADY_OWNER",
  /** Nhóm đang có phiếu đổi chủ / đổi thủ quỹ hoặc yêu cầu "chủ mất tích" đang mở (QĐ-8). */
  GOVERNANCE_PENDING: "ADMIN_GROUP_ROLE_GOVERNANCE_PENDING",
  /** Hai lượt đổi đồng thời — lượt thua phải tải lại. */
  CONCURRENT_CHANGE: "ADMIN_GROUP_ROLE_CONCURRENT_CHANGE",
} as const;
export type AdminGroupRoleCode = (typeof ADMIN_GROUP_ROLE_CODE)[keyof typeof ADMIN_GROUP_ROLE_CODE];
